<template>
  <section class="flex flex-col gap-12 items-center justify-center overflow-hidden px-4 py-16 relative w-full bg-white md:px-16">
    <div class="contact-info mx-auto w-full max-w-[1200px]">
      <div class="mb-12 flex flex-col items-center gap-1 text-center">
        <p
          v-if="overlineText"
          class="text-[14px] font-semibold uppercase leading-5 tracking-[1px]"
        >
          {{ overlineText }}
        </p>
        <h2
          class="text-[36px] font-medium leading-[44px] tracking-[-0.72px]"
        >
          {{ headingText }}
        </h2>
      </div>

      <div class="flex flex-col gap-8">
        <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
          <!-- Address card -->
          <div class="rounded-[24px] p-8" :style="{ backgroundColor: cardBackgroundColor }">
            <h3
              class="text-[32px] font-semibold leading-[40px] tracking-[-0.64px]"
            >
              {{ addressCardHeading }}
            </h3>
            <div class="mt-5 space-y-1 text-[18px] leading-[28px] text-dark">
              <p class="font-semibold">{{ businessName }}</p>
              <p>{{ addressLine1 }}</p>
              <p>{{ addressLine2 }}</p>
            </div>
            <ContactPhoneRow class="mt-5">
              <p class="text-[16px] leading-[18px]">
                <a :href="`tel:${phoneNumber}`" class="mr-1 font-semibold text-dark no-underline">{{ phoneNumber }}</a><span class="text-gray">{{ phoneHours }}</span>
              </p>
            </ContactPhoneRow>
          </div>

          <!-- Contact person card -->
          <div class="rounded-[24px] p-8" :style="{ backgroundColor: cardBackgroundColor }">
            <h3
              class="text-[32px] font-semibold leading-[40px] tracking-[-0.64px]"
            >
              {{ personCardHeading }}
            </h3>
            <div class="mt-5 flex items-center gap-3">
              <img
                v-if="personImage"
                :src="personImage"
                :alt="personName"
                class="h-16 w-16 rounded-full object-cover"
              >
              <div>
                <p class="text-[16px] font-semibold leading-[24px] text-dark">{{ personName }}</p>
                <p v-if="personDescription" class="text-[14px] leading-[20px] text-gray">{{ personDescription }}</p>
              </div>
            </div>
            <div class="mt-5 flex flex-col">
              <ContactPhoneRow v-if="personPhone">
                <p class="text-[16px] leading-[18px]">
                  <a :href="`tel:${personPhone}`" class="mr-1 font-semibold text-dark no-underline">{{ personPhone }}</a><span class="text-gray">{{ phoneHours }}</span>
                </p>
              </ContactPhoneRow>
              <ContactEmailRow v-if="personEmail">
                <a :href="`mailto:${personEmail}`" class="text-[16px] font-semibold leading-[18px] text-dark no-underline">{{ personEmail }}</a>
              </ContactEmailRow>
            </div>
          </div>
        </div>

        <!-- Bottom row: live map (from address) + optional location photo -->
        <div class="grid grid-cols-1 gap-8" :class="locationPhoto ? 'md:grid-cols-2' : ''">
          <div
            class="relative aspect-[1366/902] w-full overflow-hidden rounded-xl border border-neutral-200 transition-opacity duration-600 ease-out md:aspect-auto md:h-[386px]"
            :style="{ opacity: mapLoaded ? 1 : 0.5 }"
          >
            <iframe
              :src="mapEmbedUrl"
              :title="mapIframeTitle"
              class="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              allowfullscreen
              @load="mapLoaded = true"
            />
          </div>
          <img
            v-if="locationPhoto"
            :src="locationPhoto"
            :alt="t('entrance_alt')"
            class="aspect-[584/386] w-full rounded-xl object-cover md:aspect-auto md:h-[386px]"
          >
        </div>
      </div>
    </div>
  </section>

</template>

<script setup>
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import ContactPhoneRow from '@/Shared/ContactPhoneRow.vue';
import ContactEmailRow from '@/Shared/ContactEmailRow.vue';
import { themeCardColorFor } from '@/utils/siteThemeColors';
import { useTranslations } from '@/utils/translations.js';

const { t } = useTranslations();

const props = defineProps({
  overlineText:        { type: String, default: '' },
  headingText:         { type: String, default: 'Kontakt' },
  addressCardHeading:  { type: String, default: 'Adres i kontakt' },
  businessName:        { type: String, default: 'Centrum Medyczne CODE' },
  addressLine1:        { type: String, default: 'ul. Juliana Tuwima 15 lok. U4' },
  addressLine2:        { type: String, default: '90-010 Łódź' },
  phoneNumber:         { type: String, default: '887 220 088' },
  phoneHours:          { type: String, default: '(pon-pt: 8:00 - 20:00)' },
  personCardHeading:   { type: String, default: 'Osoba do kontaktu' },
  personName:          { type: String, default: '' },
  personDescription:   { type: String, default: '' },
  personImage:         { type: String, default: '' },
  personPhone:         { type: String, default: '' },
  personEmail:         { type: String, default: '' },
  locationPhoto:       { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context:        { type: String, default: null },
});

const page = usePage();

const cardBackgroundColor = computed(() => themeCardColorFor(props.site_context));

const mapLoaded = ref(false);

const mapQuery = computed(() => (
  [props.businessName, props.addressLine1, props.addressLine2].filter(Boolean).join(', ')
));

// Google Maps embed without an API key (the public `output=embed` endpoint).
const mapEmbedUrl = computed(() => {
  const locale = page.props.current_locale === 'en' ? 'en' : 'pl';
  return `https://www.google.com/maps?q=${encodeURIComponent(mapQuery.value)}&hl=${locale}&z=16&output=embed`;
});

const mapIframeTitle = computed(() => (
  `${props.businessName} — ${props.addressLine1}, ${props.addressLine2}`
));
</script>
